import React from 'react';
import ReactDOM from 'react-dom';
import { ChakraProvider } from '@chakra-ui/react'
import { BrowserRouter } from "react-router";
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloProvider,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import Amplify from "aws-amplify";
import { HelmetProvider } from "react-helmet-async";
import { awsconfig } from "./utils/aws-config";
import reportWebVitals from './reportWebVitals';
import { getCookie } from "./utils/cookie";
import theme from "./theme";
import App from './App';
import './index.css';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT,
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from cookies if it exists
  const token = getCookie("dyn_session");
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      "x-access-token": token,
    },
  };
});

Amplify.configure({ Auth: awsconfig });

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          engagementCourses: {
            // cache by time window and selection; ignore pagination controls
            keyArgs: [
              "publisherId",
              ["productIds"],
              "from",
              "to",
            ],
            merge(existing = { totalCount: 0, items: [] }, incoming, { args }) {
              const skip = args?.skip ?? 0;
              const incomingItems = incoming?.items ?? [];
              const mergedItems = existing?.items ? existing.items.slice(0) : [];
              for (let i = 0; i < incomingItems.length; i++) {
                mergedItems[skip + i] = incomingItems[i];
              }
              return {
                totalCount: typeof incoming?.totalCount === 'number' ? incoming.totalCount : (existing?.totalCount ?? mergedItems.length),
                items: mergedItems,
              };
            },
          },
          engagementCourseDetail: {
            // ensure course detail is separately cached per window
            keyArgs: ["productId", "from", "to"],
          },
          dynoes: {
            keyArgs: false,
            merge(existing, incoming, { args: { offset = 0 } }) {
              // Slicing is necessary because the existing data is
              // immutable, and frozen in development.
              const merged = existing ? existing.slice(0) : [];
              for (let i = 0; i < incoming.length; ++i) {
                merged[offset + i] = incoming[i];
              }
              return merged;
            },
          },
          dynoesConnectionPublisher: {
            keyArgs: false,
            merge(existing, incoming) {
              let launches = [];
              if (existing && existing.launches) {
                launches = launches.concat(existing.launches);
              }
              if (incoming && incoming.launches) {
                launches = launches.concat(incoming.launches);
              }
              return {
                ...incoming,
                launches,
              };
            },
          },
          productsConnection: {
            keyArgs: false,
            merge(existing, incoming) {
              let launches = [];
              if (existing && existing.launches) {
                launches = launches.concat(existing.launches);
              }
              if (incoming && incoming.launches) {
                launches = launches.concat(incoming.launches);
              }
              return {
                ...incoming,
                launches,
              };
            },
          },
        },
      },
      CourseEngagement: {
        keyFields: ["productId"],
      },
    },
  }),
});

ReactDOM.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <BrowserRouter>
        <ChakraProvider theme={theme}>
          <HelmetProvider>
            <App />
          </HelmetProvider>
        </ChakraProvider>
      </BrowserRouter>
    </ApolloProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
